/** 48 kHz in the browser; the WASM decimator produces the 16 kHz Whisper wants. */
const CAPTURE_RATE = 48_000;

export type GraphEvents = {
  onPcm(pcm: Int16Array): void;
  onEnded(): void;
  onStarted(): void;
};

type WorkletMessage = { type: 'pcm'; pcm: Int16Array } | { type: 'started' } | { type: 'ended' };

export type WorkletNodeLike = {
  port: {
    onmessage: ((e: { data: unknown }) => void) | null;
    postMessage(message: unknown, transfer?: Transferable[]): void;
  };
  connect(destination: unknown): void;
  disconnect(): void;
};

type SourceLike = { connect(node: WorkletNodeLike): void; disconnect(): void };

export type ContextLike = {
  readonly sampleRate: number;
  readonly destination: unknown;
  audioWorklet: { addModule(url: string): Promise<void> };
  createMediaStreamSource(stream: MediaStream): SourceLike;
  close(): Promise<void>;
};

export type GraphDeps = {
  context(sampleRate: number): ContextLike;
  node(context: ContextLike): WorkletNodeLike;
  wasm(): Promise<ArrayBuffer>;
};

// The DOM types are wider than the slices above in ways that do not matter
// here (`connect` takes an `AudioNode`), hence the casts through `unknown`.
const browser: GraphDeps = {
  context: (sampleRate) => new AudioContext({ sampleRate }) as unknown as ContextLike,
  node: (context) =>
    new AudioWorkletNode(context as unknown as AudioContext, 'capture-processor') as unknown as WorkletNodeLike,
  wasm: async () => (await fetch('/tutor_audio_bg.wasm')).arrayBuffer(),
};

/**
 * The capture side of the audio machine, without a microphone in it.
 *
 * A conversation builds one for the whole session and leases a stream to it
 * per press (media-channel design §4.3). The worklet node therefore lives
 * across presses, and its hands-free flag, its tutor-speaking flag and its
 * pipeline with it: a worklet rebuilt mid-reply would never learn the tutor
 * was speaking, because `BargeIn` posts only on a change. With no source
 * attached, the worklet's `process` gets no channel and does nothing.
 */
export class AudioGraph {
  private context: ContextLike | null = null;
  private node: WorkletNodeLike | null = null;
  private source: SourceLike | null = null;
  private stopped = false;
  // Assigned in the body: node's strip-only loader rejects parameter properties.
  private readonly events: GraphEvents;
  private readonly deps: GraphDeps;

  constructor(events: GraphEvents, deps: GraphDeps = browser) {
    this.events = events;
    this.deps = deps;
  }

  async start(): Promise<void> {
    const context = this.deps.context(CAPTURE_RATE);
    this.context = context;
    // `sampleRate` is a *hint*. A device that will not run its graph at 48 kHz
    // gives back whatever rate it prefers and says nothing, and the Rust
    // decimator's `IN_RATE` is a hardcoded 48 000: it would decimate 44.1 kHz
    // by three and label the result 16 kHz, and Whisper would transcribe
    // tempo-shifted audio as best it could. A wrong transcript with no error
    // anywhere is the worst available outcome, so this is loud.
    if (context.sampleRate !== CAPTURE_RATE) {
      const actual = context.sampleRate;
      this.stop();
      throw new Error(
        `this browser captures at ${actual} Hz, not ${CAPTURE_RATE} Hz; the resampler only handles ${CAPTURE_RATE} Hz input`,
      );
    }
    await context.audioWorklet.addModule('/worklet.js');
    // Stopped while loading: the context is closed, and a node built on it
    // would throw into whichever session is current by then.
    if (this.stopped) return;
    const node = this.deps.node(context);
    this.node = node;
    node.port.onmessage = (e) => {
      const message = e.data as WorkletMessage;
      if (message.type === 'pcm') this.events.onPcm(message.pcm);
      else if (message.type === 'started') this.events.onStarted();
      else if (message.type === 'ended') this.events.onEnded();
    };
    // The bytes, not a compiled `WebAssembly.Module`. A `Module` cannot be
    // structured-cloned into an `AudioWorkletGlobalScope`: Chrome drops the
    // message as a `messageerror`, which throws nothing on either side, so the
    // worklet silently never emits a sample.
    const wasm = await this.deps.wasm();
    if (this.stopped) return;
    node.port.postMessage({ type: 'init', wasm }, [wasm]);
    // Keeps the graph pulling without routing the microphone to the speakers:
    // the processor emits no output, so nothing is audible.
    node.connect(context.destination);
  }

  attach(stream: MediaStream): void {
    this.detach();
    if (!this.context || !this.node) return;
    const source = this.context.createMediaStreamSource(stream);
    source.connect(this.node);
    this.source = source;
  }

  detach(): void {
    this.source?.disconnect();
    this.source = null;
  }

  beginUtterance(): void {
    this.post({ type: 'begin' });
  }

  endUtterance(): void {
    this.post({ type: 'end' });
  }

  /**
   * Phase 4 spec §6. Closing an utterance that hands-free left open when it
   * turns off is the worklet's `Capture.setHandsFree`'s job: it alone knows
   * whether one is open and whether it is a manual hold.
   */
  setHandsFree(on: boolean): void {
    this.post({ type: 'handsFree', on });
  }

  /** Phase 4 spec §7: `Endpointer` holds a hands-free onset back while this is on. */
  setTutorSpeaking(on: boolean): void {
    this.post({ type: 'tutorSpeaking', on });
  }

  stop(): void {
    this.stopped = true;
    this.detach();
    void this.context?.close();
    this.context = null;
    this.node = null;
  }

  private post(message: object): void {
    this.node?.port.postMessage(message);
  }
}
